import { useCallback } from 'react'
import { useInteractToNote } from '@/hooks/api/dashboard'
import { AlbumPhoto } from '../types'
import { fireAlertToast } from '@/design-system/alerts'

/**
 * Note-scoped like — matches babylon_meshdom AlbumContent / useInteractToNote.
 * Photos that share a noteId (batch upload) correctly share like state.
 */
export function useAlbumLike(
  albumId: string | undefined,
  _photos: AlbumPhoto[],
  setPhotos: (updater: (prev: AlbumPhoto[]) => AlbumPhoto[]) => void,
) {
  const interact = useInteractToNote()

  const toggleLike = useCallback(
    async (photo: AlbumPhoto) => {
      if (!albumId || !photo.noteId) return
      const nextLiked = !photo.liked

      setPhotos((prev) =>
        prev.map((p) =>
          p.noteId === photo.noteId
            ? {
                ...p,
                liked: nextLiked,
                likeCount: Math.max(0, (p.likeCount || 0) + (nextLiked ? 1 : -1)),
              }
            : p,
        ),
      )

      try {
        await interact.mutateAsync({
          topicID: albumId,
          noteID: photo.noteId,
          comment: 'test',
          interactionType: '3',
          interaction: nextLiked ? '1' : '0',
        })
      } catch {
        setPhotos((prev) =>
          prev.map((p) =>
            p.noteId === photo.noteId
              ? {
                  ...p,
                  liked: photo.liked,
                  likeCount: photo.likeCount,
                }
              : p,
          ),
        )
        fireAlertToast('photo.likeFailed')
      }
    },
    [albumId, interact, setPhotos],
  )

  return { toggleLike, liking: interact.isLoading }
}
